export type EconomySettingValueType = 'int' | 'float' | 'string';

export type EconomySettingGroup = 'Passes' | 'TP allocations' | 'Exchange rates'
  | 'Partner task names' | 'Standard partner stakes' | 'Pro partner stakes' | 'Partner availability';

export interface EconomySettingDefinition {
  key: string;
  label: string;
  description: string;
  group: EconomySettingGroup;
  valueType: EconomySettingValueType;
  min?: number;
  max?: number;
  /** Text settings only: longest value the backend accepts. */
  maxLength?: number;
  step: string;
  unit: string;
  positive?: boolean;
  readOnly?: boolean;
  readOnlyReason?: string;
  secureDailyPassEndpoint?: boolean;
}

// Keep the browser guard aligned with the backend converter bounds.  The
// lower bound keeps the largest supported reward representable in currency
// columns, while the upper bound prevents an accidental near-zero payout.
export const ECONOMY_RATE_MIN = 0.001;
export const ECONOMY_RATE_MAX = 10000;

/**
 * The admin UI intentionally opts into this list instead of rendering every
 * row returned by /admin/settings. That keeps the control surface limited to
 * the economy rules that have an agreed product meaning.
 */
const partners = [
  { id: 'theoremreach', name: 'TheoremReach' },
  { id: 'rapidoreach', name: 'RapidoReach' },
  { id: 'adgem', name: 'AdGem' },
  { id: 'cpx', name: 'CPX Research' },
  { id: 'bitlabs', name: 'BitLabs' },
  { id: 'lootably', name: 'Lootably' },
  { id: 'offerwallgg', name: 'Offerwall.GG' },
];

// Must match PUBLIC_NAME_MAX_LENGTH / PUBLIC_SUBTITLE_MAX_LENGTH in the backend.
export const PARTNER_NAME_MAX_LENGTH = 30;
export const PARTNER_SUBTITLE_MAX_LENGTH = 60;

const partnerDefinitions: EconomySettingDefinition[] = partners.flatMap(partner => [
  {
    key: `partner_${partner.id}_public_name`, label: `${partner.name} task name`,
    description: `The name users see for ${partner.name} tasks. Never use the partner's own name. Shows the next time a user opens the task list.`,
    group: 'Partner task names', valueType: 'string', maxLength: PARTNER_NAME_MAX_LENGTH,
    step: '', unit: `max ${PARTNER_NAME_MAX_LENGTH} characters`,
  },
  {
    key: `partner_${partner.id}_public_subtitle`, label: `${partner.name} task subtitle`,
    description: `The line under the ${partner.name} task name.`,
    group: 'Partner task names', valueType: 'string', maxLength: PARTNER_SUBTITLE_MAX_LENGTH,
    step: '', unit: `max ${PARTNER_SUBTITLE_MAX_LENGTH} characters`,
  },
  ...(['standard', 'pro'] as const).map((workspace): EconomySettingDefinition => ({
    key: `partner_${partner.id}_${workspace}_stake_tp`,
    label: `${partner.name} ${workspace === 'standard' ? 'Standard' : 'Pro'} stake`,
    description: 'One verified completion unlocks this fixed workspace stake. No separate wallet reward. Changes apply to new attempts only.',
    group: workspace === 'standard' ? 'Standard partner stakes' : 'Pro partner stakes',
    valueType: 'int', min: 1, max: 1000, step: '1', unit: 'TP per workspace unlock',
  })),
  {
    key: `partner_${partner.id}_enabled`, label: `${partner.name} launches`,
    description: '0 = paused, 1 = allow launches. Approved platform configuration and verified callbacks are also required on the server.',
    group: 'Partner availability', valueType: 'int', min: 0, max: 1,
    step: '1', unit: '0 off / 1 on',
  },
]);

export const ECONOMY_SETTING_DEFINITIONS: EconomySettingDefinition[] = [
  ...partnerDefinitions,
  {
    key: 'free_passes_per_day',
    label: 'Daily free passes',
    description: 'How many free passes each user receives during one UTC day.',
    group: 'Passes',
    valueType: 'int',
    min: 0,
    max: 100,
    step: '1',
    unit: 'passes per day',
    secureDailyPassEndpoint: true,
  },
  {
    key: 'max_earned_pass_held',
    label: 'Earned-pass limit',
    description: 'The maximum number of rewarded-ad passes a user can hold.',
    group: 'Passes',
    valueType: 'int',
    min: 1,
    max: 1,
    step: '1',
    unit: 'pass held',
    readOnly: true,
    readOnlyReason:
      'The current pass bank supports one earned pass. This becomes configurable when the pass storage model changes.',
  },
  {
    key: 'passes_reset_hour_utc',
    label: 'Daily pass reset time',
    description: 'The UTC hour at which daily free passes are replenished.',
    group: 'Passes',
    valueType: 'int',
    min: 0,
    max: 23,
    step: '1',
    unit: 'UTC hour (0–23)',
  },
  {
    key: 'basic_allocation_tp',
    label: 'Basic allocation',
    description: 'TP allocated when a new Basic session starts.',
    group: 'TP allocations',
    valueType: 'int',
    min: 1,
    max: 1000,
    step: '1',
    unit: 'TP per new session',
  },
  {
    key: 'premium_allocation_tp',
    label: 'Premium allocation',
    description: 'TP allocated when a new Premium session starts.',
    group: 'TP allocations',
    valueType: 'int',
    min: 1,
    max: 1000,
    step: '1',
    unit: 'TP per new session',
  },
  {
    key: 'pro_allocation_tp',
    label: 'Pro allocation',
    description: 'TP allocated when a new Pro session starts.',
    group: 'TP allocations',
    valueType: 'int',
    min: 1,
    max: 1000,
    step: '1',
    unit: 'TP per new session',
  },
  {
    key: 'tp_per_ngn',
    label: 'TP per NGN',
    description: 'TP value used for one Nigerian naira in new financial calculations.',
    group: 'Exchange rates',
    valueType: 'float',
    step: 'any',
    unit: 'TP / NGN',
    min: ECONOMY_RATE_MIN,
    max: ECONOMY_RATE_MAX,
    positive: true,
  },
  {
    key: 'tp_per_gbp',
    label: 'TP per GBP',
    description: 'TP value used for one British pound in new financial calculations.',
    group: 'Exchange rates',
    valueType: 'float',
    step: 'any',
    unit: 'TP / GBP',
    min: ECONOMY_RATE_MIN,
    max: ECONOMY_RATE_MAX,
    positive: true,
  },
  {
    key: 'tp_per_ghs',
    label: 'TP per GHS',
    description: 'TP value used for one Ghanaian cedi in new financial calculations.',
    group: 'Exchange rates',
    valueType: 'float',
    step: 'any',
    unit: 'TP / GHS',
    min: ECONOMY_RATE_MIN,
    max: ECONOMY_RATE_MAX,
    positive: true,
  },
  {
    key: 'tp_per_cad',
    label: 'TP per CAD',
    description: 'TP value used for one Canadian dollar in new financial calculations.',
    group: 'Exchange rates',
    valueType: 'float',
    step: 'any',
    unit: 'TP / CAD',
    min: ECONOMY_RATE_MIN,
    max: ECONOMY_RATE_MAX,
    positive: true,
  },
  {
    key: 'tp_per_usd',
    label: 'TP per USD',
    description: 'TP value used for one US dollar in new financial calculations.',
    group: 'Exchange rates',
    valueType: 'float',
    step: 'any',
    unit: 'TP / USD',
    min: ECONOMY_RATE_MIN,
    max: ECONOMY_RATE_MAX,
    positive: true,
  },
  {
    key: 'tp_per_inr',
    label: 'TP per INR',
    description: 'TP value used for one Indian rupee in new financial calculations.',
    group: 'Exchange rates',
    valueType: 'float',
    step: 'any',
    unit: 'TP / INR',
    min: ECONOMY_RATE_MIN,
    max: ECONOMY_RATE_MAX,
    positive: true,
  },
  {
    key: 'tp_per_aud',
    label: 'TP per AUD',
    description: 'TP value used for one Australian dollar in new financial calculations.',
    group: 'Exchange rates',
    valueType: 'float',
    step: 'any',
    unit: 'TP / AUD',
    min: ECONOMY_RATE_MIN,
    max: ECONOMY_RATE_MAX,
    positive: true,
  },
  {
    key: 'tp_per_eur',
    label: 'TP per EUR',
    description: 'TP value used for one euro in new financial calculations.',
    group: 'Exchange rates',
    valueType: 'float',
    step: 'any',
    unit: 'TP / EUR',
    min: ECONOMY_RATE_MIN,
    max: ECONOMY_RATE_MAX,
    positive: true,
  },
  {
    key: 'tp_per_kes',
    label: 'TP per KES',
    description: 'TP value used for one Kenyan shilling in new financial calculations.',
    group: 'Exchange rates',
    valueType: 'float',
    step: 'any',
    unit: 'TP / KES',
    min: ECONOMY_RATE_MIN,
    max: ECONOMY_RATE_MAX,
    positive: true,
  },
  {
    key: 'tp_per_zar',
    label: 'TP per ZAR',
    description: 'TP value used for one South African rand in new financial calculations.',
    group: 'Exchange rates',
    valueType: 'float',
    step: 'any',
    unit: 'TP / ZAR',
    min: ECONOMY_RATE_MIN,
    max: ECONOMY_RATE_MAX,
    positive: true,
  },
];

export interface ParsedEconomySetting {
  /** Integer settings use numbers; decimal rates stay strings to preserve precision. */
  value: number | string;
  canonicalValue: string;
}

export interface EconomySettingParseResult {
  parsed?: ParsedEconomySetting;
  error?: string;
}

const DECIMAL_RATE_PATTERN = /^[+]?(?:\d+(?:\.\d*)?|\.\d+)$/;
const MAX_RATE_INTEGER_DIGITS = 12;

const canonicalizeDecimalRate = (rawValue: string): {
  canonicalValue: string;
  integerDigits: number;
  decimalDigits: number;
  isPositive: boolean;
} => {
  const unsigned = rawValue.startsWith('+') ? rawValue.slice(1) : rawValue;
  const [rawIntegerPart, rawFractionPart = ''] = unsigned.split('.');
  const integerPart = (rawIntegerPart || '0').replace(/^0+/, '') || '0';
  const fractionPart = rawFractionPart.replace(/0+$/, '');
  const canonicalValue = fractionPart
    ? `${integerPart}.${fractionPart}`
    : integerPart;

  return {
    canonicalValue,
    integerDigits: integerPart.length,
    decimalDigits: rawFractionPart.length,
    isPositive: integerPart !== '0' || fractionPart.length > 0,
  };
};

/** Validate the browser input before it crosses the authenticated API seam. */
export const parseEconomySettingValue = (
  definition: EconomySettingDefinition,
  rawValue: string
): EconomySettingParseResult => {
  const trimmed = rawValue.trim();
  if (trimmed === '') {
    return { error: `${definition.label} is required.` };
  }

  if (definition.valueType === 'string') {
    // Same normalisation as the backend: one line, single spaces.
    const text = trimmed.split(/\s+/).join(' ');
    if (definition.maxLength !== undefined && text.length > definition.maxLength) {
      return { error: `${definition.label} must be at most ${definition.maxLength} characters.` };
    }
    return { parsed: { value: text, canonicalValue: text } };
  }

  if (definition.valueType === 'float') {
    if (!DECIMAL_RATE_PATTERN.test(trimmed)) {
      return { error: `${definition.label} must be a finite decimal number.` };
    }

    const decimal = canonicalizeDecimalRate(trimmed);
    if (!decimal.isPositive) {
      return { error: `${definition.label} must be greater than zero.` };
    }
    if (decimal.integerDigits > MAX_RATE_INTEGER_DIGITS) {
      return {
        error: `${definition.label} supports at most ${MAX_RATE_INTEGER_DIGITS} integer digits.`,
      };
    }
    if (decimal.decimalDigits > 12) {
      return { error: `${definition.label} supports at most 12 decimal places.` };
    }
    const numericValue = Number(decimal.canonicalValue);
    if (!Number.isFinite(numericValue)) {
      return { error: `${definition.label} must be a finite decimal number.` };
    }
    if (definition.min !== undefined && numericValue < definition.min) {
      return { error: `${definition.label} must be at least ${definition.min}.` };
    }
    if (definition.max !== undefined && numericValue > definition.max) {
      return { error: `${definition.label} must be at most ${definition.max}.` };
    }

    return {
      parsed: {
        value: decimal.canonicalValue,
        canonicalValue: decimal.canonicalValue,
      },
    };
  }

  const value = Number(trimmed);
  if (!Number.isFinite(value)) {
    return { error: `${definition.label} must be a finite number.` };
  }

  if (definition.valueType === 'int' && !Number.isInteger(value)) {
    return { error: `${definition.label} must be a whole number.` };
  }

  if (definition.positive && value <= 0) {
    return { error: `${definition.label} must be greater than zero.` };
  }

  if (definition.min !== undefined && value < definition.min) {
    return { error: `${definition.label} must be at least ${definition.min}.` };
  }

  if (definition.max !== undefined && value > definition.max) {
    return { error: `${definition.label} must be at most ${definition.max}.` };
  }

  return {
    parsed: {
      value,
      canonicalValue: String(value),
    },
  };
};

export const getEconomySettingDefinition = (key: string) =>
  ECONOMY_SETTING_DEFINITIONS.find(definition => definition.key === key);

export const formatSettingUpdatedAt = (updatedAt: string | null | undefined): string => {
  if (!updatedAt) return 'Not available';
  const parsed = new Date(updatedAt);
  return Number.isNaN(parsed.getTime()) ? 'Not available' : parsed.toLocaleString();
};

/** Keep backend validation details visible to the operator. */
export const getApiErrorMessage = (error: unknown, fallback: string): string => {
  const responseData = (error as { response?: { data?: unknown } })?.response?.data;

  if (typeof responseData === 'string' && responseData.trim()) {
    return responseData;
  }

  if (responseData && typeof responseData === 'object') {
    const data = responseData as { detail?: unknown; message?: unknown };
    if (typeof data.detail === 'string' && data.detail.trim()) return data.detail;
    if (data.detail && typeof data.detail === 'object') {
      const detail = data.detail as { message?: unknown };
      if (typeof detail.message === 'string' && detail.message.trim()) return detail.message;
    }
    if (typeof data.message === 'string' && data.message.trim()) return data.message;
  }

  if (error instanceof Error && error.message.trim()) return error.message;
  return fallback;
};
